/**
 * Pure helpers behind the split reconcile view (#429).
 *
 * Kept out of the page component so the select-all rules — which have to cope
 * with rows locked by another reconciliation — can be tested without rendering
 * a DataTable.
 */

export interface ReconcileRowLike {
	kind: string;
	id: string;
	signedAmountCents: number;
}

/** Stable tick-set key. Ids are only unique WITHIN a kind, so both are needed. */
export const reconcileRowKey = (r: Pick<ReconcileRowLike, 'kind' | 'id'>): string => `${r.kind}:${r.id}`;

/**
 * Split a period's register rows the way a bank statement does: money out on
 * one side, money in on the other.
 *
 * Sign is the discriminator rather than `kind` because a journal adjustment or
 * a transfer can land on either side — an interest credit and a service fee are
 * both `journal` rows.
 *
 * Zero-amount rows group with deposits so a degenerate row is still visible and
 * tickable somewhere rather than vanishing from both panes.
 */
export const partitionRegisterRows = <T extends ReconcileRowLike>(
	rows: readonly T[],
): { payments: T[]; deposits: T[] } => ({
		payments: rows.filter((r) => r.signedAmountCents < 0),
		deposits: rows.filter((r) => r.signedAmountCents >= 0),
	});

/**
 * Tri-state for a pane's select-all box.
 *
 * Locked rows are excluded from the calculation entirely: they can never be
 * toggled, so counting them would leave a pane containing one locked row stuck
 * at `indeterminate` forever with no way for the user to resolve it.
 */
export const paneSelectAllStatus = <T extends ReconcileRowLike>(
	rows: readonly T[],
	isTicked: (r: T) => boolean,
	isLocked: (r: T) => boolean,
): 'checked' | 'unchecked' | 'indeterminate' => {
	const selectable = rows.filter((r) => !isLocked(r));
	if (selectable.length === 0) return 'unchecked';
	const ticked = selectable.filter(isTicked).length;
	if (ticked === 0) return 'unchecked';
	if (ticked === selectable.length) return 'checked';
	return 'indeterminate';
};

/**
 * Apply a pane's select-all: tick every selectable row, or untick them all when
 * they're already fully ticked. Returns a NEW set; never mutates the input.
 *
 * Only this pane's rows are touched — the other pane's ticks are preserved,
 * which is what makes two independent select-alls safe to run over one shared
 * tick set.
 */
export const togglePaneSelection = <T extends ReconcileRowLike>(
	current: ReadonlySet<string>,
	rows: readonly T[],
	isLocked: (r: T) => boolean,
): Set<string> => {
	const next = new Set(current);
	const selectable = rows.filter((r) => !isLocked(r));
	if (selectable.length === 0) return next;
	const allTicked = selectable.every((r) => current.has(reconcileRowKey(r)));
	for (const r of selectable) {
		const key = reconcileRowKey(r);
		if (allTicked) next.delete(key);
		else next.add(key);
	}
	return next;
};
